import { useEffect, useMemo, useState } from "react";
import { Armchair, ChevronLeft, ChevronRight, Images, Music2, PackageOpen, Play, X } from "lucide-react";
import {
  EVA_SPACE_CONCEPT_NOTE,
  EVA_SPACE_MEMORIES,
  EVA_SPACE_SCENES,
  getEvaSpaceScene,
  type EvaSpaceSceneId
} from "../../config/evaSpaceDemo";
import { assetUrl } from "../../utils/assetUrl";
import "./NightEvaSpaceDemo.css";

export function NightEvaSpaceDemo({
  onClose,
  onOpenAudioCompanion,
  onOpenVideoCompanion
}: {
  onClose: () => void;
  onOpenAudioCompanion: () => void;
  onOpenVideoCompanion: () => void;
}) {
  const [activeSceneId, setActiveSceneId] = useState<EvaSpaceSceneId>("base");
  const [albumOpen, setAlbumOpen] = useState(false);
  const [memoryIndex, setMemoryIndex] = useState(0);
  const [parcelOpen, setParcelOpen] = useState(false);
  const [seatRelaxed, setSeatRelaxed] = useState(false);
  const activeScene = getEvaSpaceScene(activeSceneId);
  const activeMemory = EVA_SPACE_MEMORIES[memoryIndex];
  const memoryImage = assetUrl(activeMemory.imageSrc);

  const neighborMemoryImages = useMemo(() => {
    const previous = (memoryIndex - 1 + EVA_SPACE_MEMORIES.length) % EVA_SPACE_MEMORIES.length;
    const next = (memoryIndex + 1) % EVA_SPACE_MEMORIES.length;
    return [EVA_SPACE_MEMORIES[previous], EVA_SPACE_MEMORIES[next]].map((item) => assetUrl(item.imageSrc));
  }, [memoryIndex]);

  useEffect(() => {
    const preloadSources = [
      ...EVA_SPACE_SCENES.map((scene) => assetUrl(scene.backgroundSrc)),
      memoryImage,
      ...neighborMemoryImages
    ];
    const images = preloadSources.map((src) => {
      const image = new Image();
      image.decoding = "async";
      image.src = src;
      return image;
    });
    return () => images.forEach((image) => {
      image.src = "";
    });
  }, [memoryImage, neighborMemoryImages]);

  useEffect(() => {
    if (!albumOpen) {
      return undefined;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setAlbumOpen(false);
      }
      if (event.key === "ArrowLeft") {
        setMemoryIndex((current) => (current - 1 + EVA_SPACE_MEMORIES.length) % EVA_SPACE_MEMORIES.length);
      }
      if (event.key === "ArrowRight") {
        setMemoryIndex((current) => (current + 1) % EVA_SPACE_MEMORIES.length);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [albumOpen]);

  function selectScene(sceneId: EvaSpaceSceneId) {
    setActiveSceneId(sceneId);
    setAlbumOpen(false);
    setParcelOpen(false);
  }

  function toggleSeatRelaxation() {
    setSeatRelaxed((current) => !current);
  }

  function showPreviousMemory() {
    setMemoryIndex((current) => (current - 1 + EVA_SPACE_MEMORIES.length) % EVA_SPACE_MEMORIES.length);
  }

  function showNextMemory() {
    setMemoryIndex((current) => (current + 1) % EVA_SPACE_MEMORIES.length);
  }

  return (
    <section className="eva-space" aria-label="EVA 生活空间">
      <img
        key={activeScene.id}
        className="eva-space__background"
        src={assetUrl(activeScene.backgroundSrc)}
        alt=""
        draggable={false}
      />
      <div className="eva-space__wash" aria-hidden="true" />

      <header className="eva-space__intro">
        <span className="eva-space__eyebrow">{activeScene.eyebrow}</span>
        <h1>{activeScene.title}</h1>
        <p>{activeScene.description}</p>
      </header>

      <button className="eva-space__close" type="button" onClick={onClose} aria-label="退出 EVA 生活空间">
        <X size={28} />
      </button>

      <nav className="eva-space__scene-switcher" aria-label="空间场景切换">
        {EVA_SPACE_SCENES.map((scene) => (
          <button
            key={scene.id}
            type="button"
            className={scene.id === activeSceneId ? "is-active" : ""}
            onClick={() => selectScene(scene.id)}
          >
            <span>{scene.label}</span>
          </button>
        ))}
      </nav>

      {activeSceneId === "base" ? (
        <div className="eva-space__base-controls" aria-label="共享空间功能开关">
          <button
            type="button"
            className={`eva-space__object-control eva-space__object-control--seat${seatRelaxed ? " is-active" : ""}`}
            onClick={toggleSeatRelaxation}
            aria-label={seatRelaxed ? "退出零重力座椅" : "开启零重力座椅"}
            aria-pressed={seatRelaxed}
          >
            <span className="eva-space__object-control-ring"><Armchair size={30} /></span>
            <span className="eva-space__object-control-label">{seatRelaxed ? "退出零重力" : "座椅零重力"}</span>
          </button>
          <button
            type="button"
            className="eva-space__object-control eva-space__object-control--video"
            onClick={onOpenVideoCompanion}
            aria-label="打开影视播放"
          >
            <span className="eva-space__object-control-ring"><Play size={30} fill="currentColor" /></span>
            <span className="eva-space__object-control-label">影视播放</span>
          </button>
          <button
            type="button"
            className="eva-space__object-control eva-space__object-control--music"
            onClick={onOpenAudioCompanion}
            aria-label="打开音乐播放"
          >
            <span className="eva-space__object-control-ring"><Music2 size={30} /></span>
            <span className="eva-space__object-control-label">音乐播放</span>
          </button>
          {seatRelaxed ? (
            <output className="eva-space__seat-feedback">座椅已进入零重力模式</output>
          ) : null}
        </div>
      ) : null}

      {activeSceneId === "memory" ? (
        <button
          type="button"
          className="eva-space__hotspot eva-space__hotspot--memory"
          onClick={() => setAlbumOpen(true)}
          aria-label="打开共同经历相册"
        >
          <span className="eva-space__hotspot-ring"><Images size={30} /></span>
          <span className="eva-space__hotspot-label">打开共同经历</span>
        </button>
      ) : null}

      {activeSceneId === "movie" ? (
        <button
          type="button"
          className="eva-space__screen-action"
          onClick={onOpenVideoCompanion}
          aria-label="进入视频搭子继续播放"
        >
          <span className="eva-space__screen-action-surface">
            <Play size={34} fill="currentColor" />
            <strong>继续播放</strong>
            <small>进入视频搭子</small>
          </span>
        </button>
      ) : null}

      {activeSceneId === "parcel" ? (
        <>
          <button
            type="button"
            className="eva-space__hotspot eva-space__hotspot--parcel"
            onClick={() => setParcelOpen((current) => !current)}
            aria-label="查看空间中的包裹事件"
          >
            <span className="eva-space__hotspot-ring"><PackageOpen size={30} /></span>
            <span className="eva-space__hotspot-label">查看空间事件</span>
          </button>
          {parcelOpen ? (
            <aside className="eva-space__parcel-card" aria-live="polite">
              <span>门口出现了新包裹</span>
              <strong>另有 2 个包裹还在驿站</strong>
              <p>今晚 21:00 前关门，要顺路取吗？</p>
            </aside>
          ) : null}
        </>
      ) : null}

      {albumOpen ? (
        <div className="eva-space-album" role="dialog" aria-modal="true" aria-label="共同经历相册">
          <img key={activeMemory.id} className="eva-space-album__image" src={memoryImage} alt="" draggable={false} />
          <div className="eva-space-album__mask" aria-hidden="true" />
          <button className="eva-space-album__close" type="button" onClick={() => setAlbumOpen(false)} aria-label="关闭相册">
            <X size={30} />
          </button>
          <button className="eva-space-album__arrow eva-space-album__arrow--previous" type="button" onClick={showPreviousMemory} aria-label="上一张记忆">
            <ChevronLeft size={46} />
          </button>
          <button className="eva-space-album__arrow eva-space-album__arrow--next" type="button" onClick={showNextMemory} aria-label="下一张记忆">
            <ChevronRight size={46} />
          </button>
          <div className="eva-space-album__copy">
            <span>概念记忆 · DEMO</span>
            <strong>{activeMemory.metric}</strong>
            <h2>{activeMemory.title}</h2>
            <p>{activeMemory.description}</p>
            <small>{EVA_SPACE_CONCEPT_NOTE}</small>
          </div>
          <div className="eva-space-album__progress" aria-label={`第 ${memoryIndex + 1} 张，共 ${EVA_SPACE_MEMORIES.length} 张`}>
            <span>{String(memoryIndex + 1).padStart(2, "0")}</span>
            <i />
            <span>{String(EVA_SPACE_MEMORIES.length).padStart(2, "0")}</span>
          </div>
          <div className="eva-space-album__thumbs" aria-label="相册缩略图">
            {EVA_SPACE_MEMORIES.map((memory, index) => (
              <button
                key={memory.id}
                type="button"
                className={index === memoryIndex ? "is-active" : ""}
                onClick={() => setMemoryIndex(index)}
                aria-label={`查看${memory.title}`}
              >
                <img src={assetUrl(memory.imageSrc)} alt="" draggable={false} />
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
}
